const loadedCache = new Set();
const inflightCache = new Map();
const preloadLinkCache = new Set();
const failedCache = new Map();

const FAILURE_COOLDOWN_MS = 3 * 60 * 1000; // Re-attempt after 3 minutes

const IMAGE_EXT_PATTERN = /\.(avif|gif|jfif|jpe?g|pjpeg|pjp|png|svg|webp|bmp|ico|cur)$/i;

const hasDOM = typeof window !== 'undefined' && typeof document !== 'undefined';

const getDocumentHead = () => {
  if (!hasDOM) return null;
  return document.head || document.getElementsByTagName('head')[0] || null;
};

const ensureUrlProtocol = (url) => {
  if (!url.startsWith('//')) return url;
  if (!hasDOM || !window.location?.protocol) {
    return `https:${url}`;
  }
  return `${window.location.protocol}${url}`;
};

export const normalizeImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  if (/^file:/i.test(trimmed)) return null; // avoid local file URLs that browsers block
  if (/^data:/i.test(trimmed)) return trimmed;
  if (/^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed)) {
    return ensureUrlProtocol(trimmed);
  }
  if (trimmed.startsWith('//')) {
    return ensureUrlProtocol(trimmed);
  }
  if (trimmed.startsWith('/')) {
    return trimmed.replace(/\/+/g, '/');
  }
  if (trimmed.startsWith('./') || trimmed.startsWith('../')) {
    return trimmed;
  }
  return `/${trimmed.replace(/^\/+/, '')}`;
};

const resolveImageValue = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const normalized = normalizeImageUrl(value);
    return normalized || null;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const resolved = resolveImageValue(item);
      if (resolved) return resolved;
    }
    return null;
  }

  if (typeof value === 'object') {
    return resolveImageValue(value.url || value.src || value.image || null);
  }

  return null;
};

export const resolvePrimaryImageUrl = (value) => resolveImageValue(value);

export const resolveImageUrls = (value) => {
  const results = [];
  const seen = new Set();

  const visit = (entry) => {
    if (!entry) return;

    if (typeof entry === 'string') {
      const normalized = normalizeImageUrl(entry);
      if (normalized && !seen.has(normalized)) {
        seen.add(normalized);
        results.push(normalized);
      }
      return;
    }

    if (Array.isArray(entry)) {
      entry.forEach(visit);
      return;
    }

    if (typeof entry === 'object') {
      visit(entry.url || entry.src || entry.image || null);
    }
  };

  visit(value);
  return results;
};

const now = () => Date.now();

const isNavigatorOffline = () => (typeof navigator !== 'undefined' ? navigator.onLine === false : false);

const isUrlInCooldown = (url) => {
  const lastFailure = failedCache.get(url);
  if (!lastFailure) return false;
  if (now() - lastFailure < FAILURE_COOLDOWN_MS) {
    return true;
  }
  failedCache.delete(url);
  return false;
};

const shouldLinkPreload = () => {
  if (!hasDOM) return false;
  const relList = (document.createElement('link').relList || {});
  return typeof relList.supports === 'function' ? relList.supports('preload') : true;
};

const appendPreloadLink = (url, options = {}) => {
  if (!shouldLinkPreload()) return;
  if (preloadLinkCache.has(url)) return;
  const head = getDocumentHead();
  if (!head) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = options.as || 'image';
  link.href = url;
  if (options.crossOrigin) {
    link.crossOrigin = options.crossOrigin;
  }
  head.appendChild(link);
  preloadLinkCache.add(url);
};

const getFetchPriority = (options = {}) => options.fetchPriority || options.fetchpriority || undefined;

export const preloadImage = (rawUrl, options = {}) => {
  const url = normalizeImageUrl(rawUrl);
  if (!url) return Promise.resolve({ url: rawUrl, status: 'skipped' });

  if (loadedCache.has(url)) {
    return Promise.resolve({ url, status: 'cached' });
  }
  if (inflightCache.has(url)) {
    return inflightCache.get(url);
  }

  if (isNavigatorOffline()) {
    return Promise.resolve({ url, status: 'offline' });
  }

  if (isUrlInCooldown(url)) {
    return Promise.resolve({ url, status: 'cooldown' });
  }

  if (!hasDOM || typeof Image === 'undefined') {
    return Promise.resolve({ url, status: 'skipped' });
  }

  if (options.linkPreload) {
    appendPreloadLink(url, options);
  }

  const promise = new Promise((resolve) => {
    try {
      const img = new Image();
      if (options.crossOrigin) {
        img.crossOrigin = options.crossOrigin;
      }
      const fetchPriority = getFetchPriority(options);
      if (fetchPriority && 'fetchPriority' in img) {
        img.fetchPriority = fetchPriority;
      }
      if (options.decoding && 'decoding' in img) {
        img.decoding = options.decoding;
      }
      img.onload = () => {
        loadedCache.add(url);
        inflightCache.delete(url);
        failedCache.delete(url);
        resolve({ url, status: 'loaded' });
      };
      img.onerror = () => {
        inflightCache.delete(url);
        failedCache.set(url, now());
        resolve({ url, status: 'error' });
      };
      img.src = url;
    } catch {
      inflightCache.delete(url);
      failedCache.set(url, now());
      resolve({ url, status: 'error' });
    }
  });

  inflightCache.set(url, promise);
  return promise;
};

export const preloadImages = (urls = [], options = {}) => {
  if (!Array.isArray(urls) || !urls.length) {
    return Promise.resolve([]);
  }
  const unique = [];
  const seen = new Set();
  urls.forEach((entry) => {
    const normalized = normalizeImageUrl(entry);
    if (!normalized) return;
    if (seen.has(normalized)) return;
    seen.add(normalized);
    unique.push(normalized);
  });
  if (!unique.length) {
    return Promise.resolve([]);
  }
  const concurrency = Math.max(1, Number(options.concurrency || 4));
  const results = new Array(unique.length);
  let nextIndex = 0;

  const runNext = async () => {
    const index = nextIndex;
    nextIndex += 1;
    if (index >= unique.length) return;
    results[index] = await preloadImage(unique[index], options);
    await runNext();
  };

  const workerCount = Math.min(concurrency, unique.length);
  return Promise.all(Array.from({ length: workerCount }, runNext)).then(() => results);
};

export const collectImageUrlsFromData = (input) => {
  if (!input) return [];
  const results = new Set();
  const visited = new WeakSet();

  const visit = (value) => {
    if (!value) return;
    if (typeof value === 'string') {
      const normalized = normalizeImageUrl(value);
      if (!normalized) return;
      const target = normalized.split('?')[0];
      if (IMAGE_EXT_PATTERN.test(target)) {
        results.add(normalized);
      }
      return;
    }

    if (typeof value !== 'object') return;

    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    if (visited.has(value)) return;
    visited.add(value);

    if (value instanceof Map) {
      value.forEach(visit);
      return;
    }

    if (value instanceof Set) {
      value.forEach(visit);
      return;
    }

    Object.values(value).forEach(visit);
  };

  visit(input);
  return Array.from(results);
};

export const isImageLikelyCached = (url) => loadedCache.has(normalizeImageUrl(url));

export const clearImageCacheState = () => {
  loadedCache.clear();
  inflightCache.clear();
  preloadLinkCache.clear();
  failedCache.clear();
};

export const imageCacheDebugState = () => ({
  loaded: Array.from(loadedCache),
  inflight: Array.from(inflightCache.keys()),
  preloadedLinks: Array.from(preloadLinkCache),
  failed: Array.from(failedCache.entries()).map(([url, lastFailure]) => ({ url, lastFailure })),
});

export default preloadImages;
